import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { LoggerService } from '../../telemetry/logger.service';
import { BackgroundOperationStatusDto } from '../models/dtos/background-operation-status.dto';
import { BackgroundOperationViewModel } from '../models/view-models/background-operation.view-model';

/**
 * Service class for managing the API calls for background operations.
 */
@Injectable({
	providedIn: 'root'
})
export class BackgroundOperationDataService {

	/*
	 * The path to the dispatched services controller.
	 */
	private static readonly CONTROLLER_PATH: string = "/api/BackgroundOperations";

	/**
	 * @constructor
	 *
	 * @param httpClient The HTTP client to use for web requests.
	 * @param logger The logging service.
	 */
	constructor(
		private readonly httpClient: HttpClient,
		private readonly logger: LoggerService
	) { }

	/**
	 * Cancels a background operation.
	 * @param operation The background operation to cancel.
	 * @returns An observable that completes when the operation is canceled.
	 */
	public CancelBackgroundOperation$(operation: BackgroundOperationViewModel): Observable<void> {
		try {
			const url: string = `${BackgroundOperationDataService.CONTROLLER_PATH}/cancel/${operation.BackgroundOperationId}?type=${operation.Type}`;
			return this.httpClient.post<void>(url, {});
		} catch (error: any) {
			this.logger.TrackError("Error canceling background operation.", error, { BackgroundOperationId: operation?.BackgroundOperationId || "Unknown ID" });
			return throwError(() => error);
		}
	}

	/**
	 * The API to get the status and result data of a background operation.
	 * @param operation The background operation to get.
	 * @returns An observable with the operation status and result data.
	 */
	public GetBackgroundOperationStatus$(operation: BackgroundOperationViewModel): Observable<BackgroundOperationStatusDto> {
		try {
			const url: string = `${BackgroundOperationDataService.CONTROLLER_PATH}/status/${operation.BackgroundOperationId}?type=${operation.Type}`;
			return this.httpClient.get<BackgroundOperationStatusDto>(url, { withCredentials: true });
		} catch (error: any) {
			this.logger.TrackError("Error getting background operation status.", error, { BackgroundOperationId: operation?.BackgroundOperationId || "Unknown ID" });
			return throwError(() => error);
		}
	}
}